import { OfferLettersService } from 'api-clients/monolith/services/OfferLettersService';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { MutationOptions, useApiServiceMutation } from 'hooks/useApiServiceMutation';

import { serverErrorMessage } from '../errors';
import messages from '../messages';
import { EditedKeys } from '../types';

export type OfferData = Awaited<
  ReturnType<typeof OfferLettersService.postInternalApiApplicantsOfferLetters>
>;

type OfferOptions = MutationOptions<OfferData, unknown>;

export interface OfferValuesInput {
  offer_letter_template_id?: string;
  /** Template field key -> value. */
  field_values?: Record<string, string>;
  /**
   * Data key -> value, for the keys driving the template's conditional sections
   * (AXHE-4001). A separate map from `field_values` because it addresses a
   * different key space. Omitting the key on an update leaves the stored answers
   * alone; an empty object clears them.
   */
  condition_values?: Record<string, string>;
  /**
   * Which controls the recruiter moved off the value the form displayed
   * (AXHE-4673) — see `SendOffer/values.ts#editedKeys`. Write-back to the
   * applicant needs this AND the server's own recomputation of the prefill to
   * agree, so omitting it writes nothing back rather than writing the wrong
   * thing.
   */
  edited_keys?: EditedKeys;
}

/** Create a draft offer + render its preview (AXHE-3718 create). */
export const useCreateOffer = (applicantExternalId: string, options?: OfferOptions) => {
  const run = useCallback(
    (input: OfferValuesInput) =>
      OfferLettersService.postInternalApiApplicantsOfferLetters(applicantExternalId, input),
    [applicantExternalId],
  );

  return useApiServiceMutation<OfferData, typeof run>(run, options);
};

/** Edit an existing offer's values + re-render (AXHE-3718 update). */
export const useUpdateOffer = (externalId: string, options?: OfferOptions) => {
  const run = useCallback(
    (input: Omit<OfferValuesInput, 'offer_letter_template_id'>) =>
      OfferLettersService.patchInternalApiOfferLetters(externalId, input),
    [externalId],
  );

  return useApiServiceMutation<OfferData, typeof run>(run, options);
};

/**
 * Submit per the resolved CTA — send-for-approval vs send-to-candidate.
 *
 * `approvalPlanDigest` is `approval.plan.digest` from the preview the person is
 * looking at. With it, the server refuses a submit that would route the offer
 * differently from that preview, and answers 409 with the offer as it stands.
 */
export const useSubmitOffer = (externalId: string, options?: OfferOptions) => {
  const run = useCallback(
    (approvalPlanDigest?: string | null) =>
      approvalPlanDigest
        ? OfferLettersService.postInternalApiOfferLettersSubmitForApproval(externalId, {
            approval_plan_digest: approvalPlanDigest,
          })
        : OfferLettersService.postInternalApiOfferLettersSubmitForApproval(externalId),
    [externalId],
  );

  return useApiServiceMutation<OfferData, typeof run>(run, options);
};

export type ApprovalPlanData = Awaited<
  ReturnType<typeof OfferLettersService.postInternalApiOfferLettersApprovalPlan>
>;

/**
 * What a submit would do once an edit is saved, asked before saving it. The
 * server applies the values in memory and writes nothing.
 */
export const useApprovalPlanForEdit = (
  externalId: string,
  options?: MutationOptions<ApprovalPlanData, unknown>,
) => {
  const run = useCallback(
    (input: Pick<OfferValuesInput, 'field_values' | 'condition_values'>) =>
      OfferLettersService.postInternalApiOfferLettersApprovalPlan(externalId, input),
    [externalId],
  );

  return useApiServiceMutation<ApprovalPlanData, typeof run>(run, options);
};

/**
 * Send an offer its stage held at approval: the offer moves to `approved` and
 * its delivery is queued, without going back through approval. The response is
 * the updated preview payload.
 */
export const useSendHeldOffer = (externalId: string, options?: OfferOptions) => {
  const run = useCallback(
    () => OfferLettersService.postInternalApiOfferLettersSendToApplicant(externalId),
    [externalId],
  );

  return useApiServiceMutation<OfferData, typeof run>(run, options);
};

/**
 * Cancel an offer outright (AXHE-4682): a sent offer's signing request is
 * voided, a pending approval request is withdrawn, and the offer is marked
 * canceled. The response is the updated preview payload.
 */
export const useCancelOffer = (externalId: string, options?: OfferOptions) => {
  const run = useCallback(
    () => OfferLettersService.postInternalApiOfferLettersCancel(externalId),
    [externalId],
  );

  return useApiServiceMutation<OfferData, typeof run>(run, options);
};

/**
 * Open a revision of a sent offer (AXHE-4425): a new draft that supersedes the
 * delivered document, pre-populated with its values and rendered from the
 * frozen document the applicant is holding rather than the live template. The
 * response is the revision's own preview payload.
 *
 * Nothing the applicant can see changes here. The predecessor stays `sent` and
 * stays signable until the revision is re-sent through `useSubmitOffer`, which
 * is what retires it — the ticket's "at that moment, and not before".
 *
 * Idempotent by design: pressing Edit again while a revision is open returns
 * that revision instead of minting a rival draft, so a double-click cannot
 * orphan what the recruiter already typed.
 */
export const useReviseOffer = (externalId: string, options?: OfferOptions) => {
  const run = useCallback(
    () => OfferLettersService.postInternalApiOfferLettersRevise(externalId),
    [externalId],
  );

  return useApiServiceMutation<OfferData, typeof run>(run, options);
};

/**
 * Throw an unsent revision away (AXHE-4425), as if Edit had never been pressed.
 * The offer it supersedes is untouched and stays live for the applicant.
 *
 * Returns nothing — a 204. The endpoint accepts ONLY an unsent revision, and it
 * is deliberately not idempotent: every read path is scoped past the soft
 * delete, so a second DELETE on the same id is a 404. The container treats that
 * 404 as "already discarded" rather than retrying, which is why this hook
 * reports the outcome instead of flashing it.
 */
export type DiscardRevisionResult = { ok: true } | { ok: false; message: string };

export const useDiscardRevision = (externalId: string) => {
  const intl = useIntl();
  const [isDiscarding, setIsDiscarding] = useState(false);

  const discardRevision = useCallback(async (): Promise<DiscardRevisionResult> => {
    setIsDiscarding(true);
    try {
      await OfferLettersService.deleteInternalApiOfferLetters(externalId);
      return { ok: true };
    } catch (error) {
      // A 404 means the revision is already gone — from a retry, or from a
      // second tab. The edit the recruiter asked to drop IS dropped, so this
      // reports success rather than putting a failure in front of them for an
      // outcome they already have.
      if ((error as { status?: number } | null)?.status === 404) return { ok: true };
      return {
        ok: false,
        message:
          serverErrorMessage((error as { body?: unknown } | null)?.body) ??
          intl.formatMessage(messages.discardRevisionError),
      };
    } finally {
      setIsDiscarding(false);
    }
  }, [externalId, intl]);

  return { discardRevision, isDiscarding };
};

/**
 * Throw away an edit of an offer its stage held at approval. Previewing the edit
 * saved it, so the server puts back what the held offer said and holds it again
 * under the approval it already had.
 *
 * Reports its outcome like `useDiscardRevision`, for the same reason: the
 * caller decides where a refusal is shown. `editGone` says nothing is left to
 * put back, because a submit, a replaced document or a newer approval moved
 * the offer on, and no retry can succeed.
 */
export type DiscardHeldEditResult =
  | { ok: true }
  | { ok: false; message: string; editGone: boolean };

// The status cannot tell a gone edit from a kept one: the refusal and a failed
// render both answer 422, and a failed render rolls the restore back. So the
// server's own `can_discard_edit` decides, read after the failure. A read that
// fails decides nothing, and the edit is kept.
const heldEditGone = async (externalId: string): Promise<boolean> => {
  try {
    const offer = await OfferLettersService.getInternalApiOfferLetters(externalId);
    return (offer as { can_discard_edit?: unknown } | null)?.can_discard_edit === false;
  } catch {
    return false;
  }
};

export const useDiscardHeldEdit = (externalId: string) => {
  const intl = useIntl();
  const [isDiscarding, setIsDiscarding] = useState(false);

  const discardHeldEdit = useCallback(async (): Promise<DiscardHeldEditResult> => {
    setIsDiscarding(true);
    try {
      await OfferLettersService.postInternalApiOfferLettersDiscardEdit(externalId);
      return { ok: true };
    } catch (error) {
      return {
        ok: false,
        message:
          serverErrorMessage((error as { body?: unknown } | null)?.body) ??
          intl.formatMessage(messages.discardRevisionError),
        editGone: await heldEditGone(externalId),
      };
    } finally {
      setIsDiscarding(false);
    }
  }, [externalId, intl]);

  return { discardHeldEdit, isDiscarding };
};
